/**
 * Refer to https://github.com/enzymejs/enzyme/issues/2176 for how to test React Contexts with a shallow
 * enzyme renderer
 */
import type { ReactNode } from "react";

import { createContext, useReducer, use } from "react";
import Page from "../../models/Page";

const initialState: State = {
  pageIndex: 0,
};
type PageProviderProps = {
  children: ReactNode;
};
type State = {
  pageIndex: number;
};
type Action = { pageIndex: number } | number;
type Dispatch = (action: Action) => void;

const PageStateContext = createContext<State | undefined>(undefined);
PageStateContext.displayName = "PageStateContext";
const PageDispatchContext = createContext<Dispatch | undefined>(undefined);
PageDispatchContext.displayName = "PageDispatchContext";

function PageReducer(state: State, action: Action): State {
  const newPageIndex = Number.isInteger(action)
    ? (action as number)
    : (action as { pageIndex: number }).pageIndex;
  if (Number.isNaN(newPageIndex)) {
    // ignore
    console.error("NaN passed to PageReducer");
    return state;
  }

  return {
    pageIndex: newPageIndex,
  };
}
const PageProvider = (props: PageProviderProps) => {
  const [state, dispatch] = useReducer(PageReducer, initialState);

  return (
    <PageStateContext value={state}>
      <PageDispatchContext value={dispatch}>
        {props.children}
      </PageDispatchContext>
    </PageStateContext>
  );
};
function usePageState() {
  const context = use(PageStateContext);
  if (context === undefined) {
    throw new Error("usePageState must be used within a PageProvider");
  }
  return context;
}
function usePageDispatch() {
  const context = use(PageDispatchContext);
  if (context === undefined) {
    throw new Error("usePageDispatch must be used within a PageProvider");
  }
  return context;
}
/**
 * When needing to access the Page Provider from within a Class based component, use this
 */
function PageConsumer({ children }: any) {
  return (
    <PageStateContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error("PageConsumer must be used within a PageProvider");
        }
        return children(context);
      }}
    </PageStateContext.Consumer>
  );
}
export { PageProvider, usePageState, usePageDispatch, PageConsumer };
export { State as PageProviderState };
export { Action as PageProviderAction };
/**
 * Exported so RemoteImageViewer can access and update state
 */
export { PageStateContext, PageDispatchContext };
